
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import RecentReviewsSlider from './RecentReviewsSlider';

const RESERVATION_PASSWORD = 'yokota0809';
const RESERVATION_SESSION_KEY = 'kominka_reservation_authed';

export default function ReservationSection() {
  const { t } = useTranslation();

  const [resAuthed, setResAuthed] = useState(() => {
    return sessionStorage.getItem(RESERVATION_SESSION_KEY) === 'true';
  });
  const [resPassword, setResPassword] = useState('');
  const [resPwError, setResPwError] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    checkin: '',
    checkout: '',
    guests: '',
    purpose: '',
    luggage_before_checkin: false,
    luggage_after_checkout: false,
    luggage_small: '',
    luggage_medium: '',
    luggage_large: '',
    driver_requested: false,
    driver_date: '',
    driver_time: '',
    driver_passengers: '',
    driver_service: '',
    driver_pickup: '',
    driver_dropoff: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const showLuggageDetail = formData.luggage_before_checkin || formData.luggage_after_checkout;

  const handleResLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (resPassword === RESERVATION_PASSWORD) {
      setResAuthed(true);
      setResPwError(false);
      sessionStorage.setItem(RESERVATION_SESSION_KEY, 'true');
    } else {
      setResPwError(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('checkin', formData.checkin);
      formBody.append('checkout', formData.checkout);
      formBody.append('guests', formData.guests);
      formBody.append('purpose', formData.purpose);

      const luggageOptions: string[] = [];
      if (formData.luggage_before_checkin) luggageOptions.push('チェックイン前預け入れ');
      if (formData.luggage_after_checkout) luggageOptions.push('チェックアウト後預け入れ');
      formBody.append('luggage_storage', luggageOptions.length > 0 ? luggageOptions.join('、') : 'なし');

      if (showLuggageDetail) {
        const details: string[] = [];
        if (formData.luggage_small) details.push(`Small×${formData.luggage_small}`);
        if (formData.luggage_medium) details.push(`Medium×${formData.luggage_medium}`);
        if (formData.luggage_large) details.push(`Large×${formData.luggage_large}`);
        formBody.append('luggage_detail', details.length > 0 ? details.join('、') : '未記入');
      }

      formBody.append('driver_requested', formData.driver_requested ? 'あり' : 'なし');
      if (formData.driver_requested) {
        formBody.append('driver_service', formData.driver_service || '未選択');
        formBody.append('driver_date', formData.driver_date || '未記入');
        formBody.append('driver_time', formData.driver_time || '未記入');
        formBody.append('driver_passengers', formData.driver_passengers || '未記入');
        formBody.append('driver_pickup', formData.driver_pickup || '未記入');
        formBody.append('driver_dropoff', formData.driver_dropoff || '未記入');
      }

      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6hedbd4ieqv1pmm57t0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      if (response.ok) {
        setStatus('success');
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement;
    if (target.type === 'checkbox') {
      setFormData({ ...formData, [target.name]: target.checked });
    } else {
      setFormData({ ...formData, [target.name]: target.value });
    }
  };

  const inputStyle = {
    border: '2px solid #c8a96e',
    background: '#fff',
    color: '#3d1f00',
  };

  if (!resAuthed) {
    return (
      <section
        id="reservation"
        className="py-12 sm:py-24 relative overflow-hidden flex items-center justify-center"
        style={{
          background: 'linear-gradient(135deg, #fdf6e3 0%, #f5e6c8 50%, #ede0c4 100%)',
          fontFamily: "'Noto Serif JP', serif",
          minHeight: '60vh',
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #8B2500, #c8a96e, #8B2500)' }} />
        <div className="w-full max-w-sm mx-auto px-4">
          <div
            className="rounded-2xl p-8 relative overflow-hidden"
            style={{
              background: '#faf5e8',
              border: '3px solid #8B2500',
              boxShadow: '0 8px 30px rgba(139,37,0,0.2)',
            }}
          >
            <div className="absolute top-0 left-0 right-0 h-2" style={{ background: 'repeating-linear-gradient(90deg, #8B2500 0px, #8B2500 20px, #c8a96e 20px, #c8a96e 40px)' }} />
            <div className="text-center mb-8">
              <div className="w-14 h-14 flex items-center justify-center rounded-full mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #8B2500, #c8504a)' }}>
                <i className="ri-lock-2-line text-2xl text-white"></i>
              </div>
              <h2 className="text-xl font-extrabold" style={{ color: '#3d1f00' }}>予約フォーム</h2>
              <p className="text-xs mt-1" style={{ color: '#9a7a50' }}>パスワードを入力してください</p>
            </div>
            <form onSubmit={handleResLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-bold mb-2" style={{ color: '#3d1f00' }}>パスワード</label>
                <input
                  type="password"
                  value={resPassword}
                  onChange={(e) => setResPassword(e.target.value)}
                  placeholder="パスワードを入力"
                  className="w-full px-4 py-3 text-sm rounded-lg outline-none transition-all"
                  style={{ border: `2px solid ${resPwError ? '#c8504a' : '#c8a96e'}`, background: '#fff', color: '#3d1f00' }}
                  onFocus={(e) => (e.target.style.borderColor = '#8B2500')}
                  onBlur={(e) => (e.target.style.borderColor = resPwError ? '#c8504a' : '#c8a96e')}
                />
                {resPwError && <p className="text-xs mt-1" style={{ color: '#c8504a' }}>パスワードが正しくありません</p>}
              </div>
              <button
                type="submit"
                className="w-full py-3 rounded-lg font-bold text-sm text-white transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
                style={{ background: 'linear-gradient(135deg, #8B2500, #c8504a)', border: '2px solid #6b1c00' }}
              >
                <i className="ri-lock-unlock-line mr-2"></i>予約フォームを開く
              </button>
            </form>
            <p className="text-center text-xs mt-4" style={{ color: '#c8a96e' }}>※ パスワードはホストよりお知らせします</p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #c8a96e, #8B2500, #c8a96e)' }} />
      </section>
    );
  }

  return (
    <section
      id="reservation"
      className="py-12 sm:py-24 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #fdf6e3 0%, #f5e6c8 50%, #ede0c4 100%)',
        fontFamily: "'Noto Serif JP', serif",
      }}
    >
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #8B2500, #c8a96e, #8B2500)' }} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {status === 'success' ? (
          <div className="text-center py-8 sm:py-16">
            <div className="relative inline-flex items-center justify-center mb-8">
              <div className="w-24 h-24 rounded-full flex items-center justify-center shadow-2xl" style={{ background: 'linear-gradient(135deg, #8B2500 0%, #c8a96e 100%)', boxShadow: '0 12px 40px rgba(139,37,0,0.35)' }}>
                <i className="ri-checkbox-circle-line text-5xl" style={{ color: '#fdf6e3' }}></i>
              </div>
              <div className="absolute w-32 h-32 rounded-full animate-ping opacity-20" style={{ border: '2px solid #8B2500' }} />
            </div>
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-px w-16" style={{ background: 'linear-gradient(to right, transparent, #c8a96e)' }} />
              <span className="text-xs tracking-[0.3em] font-bold" style={{ color: '#8B2500' }}>◆ THANK YOU ◆</span>
              <div className="h-px w-16" style={{ background: 'linear-gradient(to left, transparent, #c8a96e)' }} />
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4" style={{ color: '#3d1f00', letterSpacing: '0.05em' }}>ありがとうございます</h2>
            <p className="text-base sm:text-lg font-bold mb-2" style={{ color: '#8B2500' }}>予約リクエストを受け付けました</p>
            <p className="text-sm sm:text-base mb-10" style={{ color: '#6b4c2a' }}>
              24時間以内にご登録のメールアドレスへご連絡いたします。<br />迷惑メールフォルダもご確認ください。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold text-base transition-all hover:scale-105 shadow-lg whitespace-nowrap" style={{ background: 'linear-gradient(135deg, #8B2500, #c8504a)', color: '#fff', border: '3px solid #6b1c00' }}>
                <i className="ri-home-line text-lg"></i>トップページへ戻る
              </a>
              <button type="button" onClick={() => setStatus('idle')} className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold text-base transition-all hover:scale-105 whitespace-nowrap cursor-pointer" style={{ background: 'rgba(255,255,255,0.9)', color: '#3d1f00', border: '2px solid #c8a96e' }}>
                <i className="ri-edit-line text-lg"></i>別の日程で予約する
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-center mb-10 sm:mb-16">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#8B2500]" />
                <span className="text-xs tracking-[0.3em] font-bold" style={{ color: '#8B2500' }}>◆ RESERVATION ◆</span>
                <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#8B2500]" />
              </div>
              <h2 className="text-3xl sm:text-5xl font-extrabold mb-4 sm:mb-6" style={{ color: '#3d1f00', letterSpacing: '0.05em' }}>宿泊予約</h2>
              <p className="text-base sm:text-lg mb-6 sm:mb-8" style={{ color: '#6b4c2a' }}>昭和レトロな古民家で特別なひとときを</p>
              <div
                className="mt-4 sm:mt-5 mx-auto max-w-md rounded-xl px-4 py-3 flex items-center justify-center gap-2 sm:gap-3"
                style={{ background: 'linear-gradient(135deg, #3d1f00 0%, #6b3a1f 100%)', border: '2px solid #c8a96e', boxShadow: '0 4px 20px rgba(61,31,0,0.3)' }}
              >
                <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <p className="text-xs sm:text-sm font-bold text-center" style={{ color: '#f5e6c8' }}>
                  直接予約は <span className="text-yellow-300">Airbnbより最大15%OFF</span> — 空きがなくなる前にご予約を
                </p>
              </div>
            </div>

            <div className="mb-10 sm:mb-12">
              <p className="text-center text-sm mb-4 uppercase tracking-wide font-bold" style={{ color: '#8B2500' }}>◆ オンライン予約 ◆</p>
              <div className="flex justify-center">
                <a
                  href="https://www.airbnb.jp/magome-sta-ota-city-japan/stays"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex items-center justify-center gap-3 px-6 sm:px-8 py-4 rounded-lg transition-all hover:scale-105 shadow-lg w-full max-w-md relative overflow-hidden"
                  style={{ background: 'linear-gradient(135deg, #c8504a 0%, #e85d54 100%)', border: '3px solid #8B2500' }}
                >
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-home-heart-line text-2xl text-white"></i>
                  </div>
                  <div className="text-left">
                    <p className="text-base font-bold text-white">Airbnbで予約</p>
                    <p className="text-xs text-white opacity-90">即時予約・空室確認</p>
                  </div>
                  <i className="ri-external-link-line text-lg ml-auto text-white"></i>
                </a>
              </div>
              <div className="flex items-center gap-4 my-6 sm:my-8">
                <div className="flex-1 h-px" style={{ background: 'linear-gradient(to right, transparent, #c8a96e, transparent)' }}></div>
                <span className="text-xs sm:text-sm uppercase tracking-wide font-bold whitespace-nowrap" style={{ color: '#8B2500' }}>または直接予約</span>
                <div className="flex-1 h-px" style={{ background: 'linear-gradient(to right, transparent, #c8a96e, transparent)' }}></div>
              </div>
            </div>

            <div className="mb-6 sm:mb-8 rounded-xl p-4 sm:p-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #fff8e1 0%, #ffe9b8 100%)', border: '3px solid #c8a96e', boxShadow: '0 4px 15px rgba(139,37,0,0.15)' }}>
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: 'linear-gradient(135deg, #8B2500, #c8504a)' }}>
                  <i className="ri-star-line text-lg sm:text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold mb-2" style={{ color: '#3d1f00' }}>直接予約の特典</h3>
                  <ul className="space-y-1 text-xs sm:text-sm" style={{ color: '#4a3728' }}>
                    <li className="flex items-center gap-2"><i className="ri-check-line flex-shrink-0" style={{ color: '#8B2500' }}></i><span>最安値保証 — プラットフォーム手数料なし</span></li>
                    <li className="flex items-center gap-2"><i className="ri-check-line flex-shrink-0" style={{ color: '#8B2500' }}></i><span>チェックイン・アウト時間の柔軟対応</span></li>
                    <li className="flex items-center gap-2"><i className="ri-check-line flex-shrink-0" style={{ color: '#8B2500' }}></i><span>ホストと直接コミュニケーション</span></li>
                    <li className="flex items-center gap-2"><i className="ri-check-line flex-shrink-0" style={{ color: '#8B2500' }}></i><span>長期滞在割引あり</span></li>
                  </ul>
                </div>
              </div>
            </div>

            <RecentReviewsSlider />

            {/* フォームカード */}
            <div className="rounded-2xl p-5 sm:p-10 relative overflow-hidden mt-8" style={{ background: 'linear-gradient(to bottom, #faf5e8 0%, #f5ead5 100%)', border: '4px solid #8B2500', boxShadow: '0 8px 30px rgba(139,37,0,0.2)' }}>
              <div className="absolute top-0 left-0 right-0 h-2" style={{ background: 'repeating-linear-gradient(90deg, #8B2500 0px, #8B2500 20px, #c8a96e 20px, #c8a96e 40px)' }} />
              <div className="absolute bottom-0 left-0 right-0 h-2" style={{ background: 'repeating-linear-gradient(90deg, #c8a96e 0px, #c8a96e 20px, #8B2500 20px, #8B2500 40px)' }} />

              <div className="text-center mb-6 sm:mb-8">
                <div className="inline-block px-5 sm:px-6 py-2 rounded-full mb-4" style={{ background: 'linear-gradient(135deg, #8B2500, #c8504a)', color: '#fff' }}>
                  <span className="text-sm font-bold tracking-wider">◆ 予約申込書 ◆</span>
                </div>
              </div>

              <form id="reservation-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-bold mb-2" style={{ color: '#3d1f00' }}>お名前 <span style={{ color: '#c8504a' }}>*</span></label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required placeholder="山田 太郎"
                      className="w-full px-4 py-3.5 text-sm rounded-lg outline-none transition-all" style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = '#8B2500')} onBlur={(e) => (e.target.style.borderColor = '#c8a96e')} />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-bold mb-2" style={{ color: '#3d1f00' }}>メールアドレス <span style={{ color: '#c8504a' }}>*</span></label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required placeholder="example@mail.com"
                      className="w-full px-4 py-3.5 text-sm rounded-lg outline-none transition-all" style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = '#8B2500')} onBlur={(e) => (e.target.style.borderColor = '#c8a96e')} />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-bold mb-2" style={{ color: '#3d1f00' }}>電話番号 <span style={{ color: '#c8504a' }}>*</span></label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required placeholder="090-1234-5678"
                    className="w-full px-4 py-3.5 text-sm rounded-lg outline-none transition-all" style={inputStyle}
                    onFocus={(e) => (e.target.style.borderColor = '#8B2500')} onBlur={(e) => (e.target.style.borderColor = '#c8a96e')} />
                </div>

                {/* 宿泊日程 - シンプルなdate input */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label htmlFor="checkin" className="block text-sm font-bold mb-2" style={{ color: '#3d1f00' }}>
                      チェックイン <span style={{ color: '#c8504a' }}>*</span>
                    </label>
                    <input type="date" id="checkin" name="checkin" value={formData.checkin} onChange={handleChange} required
                      className="w-full px-4 py-3.5 text-sm rounded-lg outline-none transition-all cursor-pointer" style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = '#8B2500')} onBlur={(e) => (e.target.style.borderColor = '#c8a96e')} />
                  </div>
                  <div>
                    <label htmlFor="checkout" className="block text-sm font-bold mb-2" style={{ color: '#3d1f00' }}>
                      チェックアウト <span style={{ color: '#c8504a' }}>*</span>
                    </label>
                    <input type="date" id="checkout" name="checkout" value={formData.checkout} onChange={handleChange} required
                      min={formData.checkin || undefined}
                      className="w-full px-4 py-3.5 text-sm rounded-lg outline-none transition-all cursor-pointer" style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = '#8B2500')} onBlur={(e) => (e.target.style.borderColor = '#c8a96e')} />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label htmlFor="guests" className="block text-sm font-bold mb-2" style={{ color: '#3d1f00' }}>宿泊人数 <span style={{ color: '#c8504a' }}>*</span></label>
                    <select id="guests" name="guests" value={formData.guests} onChange={handleChange} required
                      className="w-full px-4 py-3.5 text-sm rounded-lg outline-none transition-all cursor-pointer" style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = '#8B2500')} onBlur={(e) => (e.target.style.borderColor = '#c8a96e')}>
                      <option value="">人数を選択</option>
                      {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={String(n)}>{n}名</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="purpose" className="block text-sm font-bold mb-2" style={{ color: '#3d1f00' }}>滞在目的 <span style={{ color: '#c8504a' }}>*</span></label>
                    <select id="purpose" name="purpose" value={formData.purpose} onChange={handleChange} required
                      className="w-full px-4 py-3.5 text-sm rounded-lg outline-none transition-all cursor-pointer" style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = '#8B2500')} onBlur={(e) => (e.target.style.borderColor = '#c8a96e')}>
                      <option value="">目的を選択</option>
                      <option value="Tourism">観光・旅行</option>
                      <option value="Workation">ワーケーション</option>
                      <option value="Long Stay">長期滞在（7泊以上）</option>
                      <option value="Business">出張</option>
                      <option value="Photoshoot">撮影利用</option>
                      <option value="Other">その他</option>
                    </select>
                  </div>
                </div>

                {/* 荷物保管セクション */}
                <div className="rounded-xl p-4 sm:p-6 space-y-4" style={{ background: 'linear-gradient(135deg, #fff8e1 0%, #fef3e2 100%)', border: '2px solid #c8a96e' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'linear-gradient(135deg, #8B2500, #c8504a)' }}>
                      <i className="ri-luggage-cart-fill text-white text-base"></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold" style={{ color: '#3d1f00' }}>荷物保管サービス（オプション）</p>
                      <p className="text-xs" style={{ color: '#8b6f47' }}>Luggage Storage — Optional Add-on</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <div className="relative mt-0.5 flex-shrink-0">
                        <input type="checkbox" name="luggage_before_checkin" checked={formData.luggage_before_checkin} onChange={handleChange} className="sr-only" />
                        <div className="w-6 h-6 rounded border-2 flex items-center justify-center transition-all" style={{ borderColor: formData.luggage_before_checkin ? '#8B2500' : '#c8a96e', background: formData.luggage_before_checkin ? '#8B2500' : '#fff' }}>
                          {formData.luggage_before_checkin && <i className="ri-check-line text-white text-sm"></i>}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-bold mb-1" style={{ color: '#3d1f00' }}>チェックイン前に預ける</p>
                        <p className="text-xs mt-0.5" style={{ color: '#8b6f47' }}>Drop off before check-in (15:00)</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <div className="relative mt-0.5 flex-shrink-0">
                        <input type="checkbox" name="luggage_after_checkout" checked={formData.luggage_after_checkout} onChange={handleChange} className="sr-only" />
                        <div className="w-6 h-6 rounded border-2 flex items-center justify-center transition-all" style={{ borderColor: formData.luggage_after_checkout ? '#8B2500' : '#c8a96e', background: formData.luggage_after_checkout ? '#8B2500' : '#fff' }}>
                          {formData.luggage_after_checkout && <i className="ri-check-line text-white text-sm"></i>}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-bold" style={{ color: '#3d1f00' }}>チェックアウト後に預ける</p>
                        <p className="text-xs mt-0.5" style={{ color: '#8b6f47' }}>Store after check-out (11:00)</p>
                      </div>
                    </label>
                  </div>
                  {showLuggageDetail && (
                    <div className="mt-4 rounded-lg p-4 space-y-3" style={{ background: '#fff', border: '1.5px dashed #c8a96e' }}>
                      <p className="text-xs font-bold mb-3" style={{ color: '#8B2500' }}>◆ 荷物の個数をご入力ください（任意）</p>
                      <div className="grid grid-cols-3 gap-2 sm:gap-3">
                        {[
                          { key: 'luggage_small', label: 'Small', price: '¥300/個' },
                          { key: 'luggage_medium', label: 'Medium', price: '¥500/個' },
                          { key: 'luggage_large', label: 'Large', price: '¥800/個' },
                        ].map(({ key, label, price }) => (
                          <div key={key} className="text-center">
                            <p className="text-xs font-bold mb-1" style={{ color: '#3d1f00' }}>{label}</p>
                            <input type="number" name={key} value={formData[key as keyof typeof formData] as string} onChange={handleChange} min="0" max="10" placeholder="0"
                              className="w-full px-2 py-2.5 text-sm text-center rounded-lg outline-none transition-all"
                              style={{ border: '2px solid #c8a96e', background: '#fff', color: '#3d1f00' }}
                              onFocus={(e) => (e.target.style.borderColor = '#8B2500')} onBlur={(e) => (e.target.style.borderColor = '#c8a96e')} />
                            <p className="text-xs mt-1 font-medium" style={{ color: '#c8504a' }}>{price}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* プライベートドライバーセクション */}
                <div className="rounded-xl p-4 sm:p-6 space-y-4" style={{ background: 'linear-gradient(135deg, #fff8e1 0%, #fef3e2 100%)', border: '2px solid #c8a96e' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'linear-gradient(135deg, #8B2500, #c8504a)' }}>
                      <i className="ri-car-line text-white text-base"></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold" style={{ color: '#3d1f00' }}>プライベートドライバー（オプション）</p>
                      <p className="text-xs" style={{ color: '#8b6f47' }}>Private Driver Service — Optional Add-on</p>
                    </div>
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <div className="relative mt-0.5 flex-shrink-0">
                      <input type="checkbox" name="driver_requested" checked={formData.driver_requested} onChange={handleChange} className="sr-only" />
                      <div className="w-6 h-6 rounded border-2 flex items-center justify-center transition-all" style={{ borderColor: formData.driver_requested ? '#8B2500' : '#c8a96e', background: formData.driver_requested ? '#8B2500' : '#fff' }}>
                        {formData.driver_requested && <i className="ri-check-line text-white text-sm"></i>}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-bold" style={{ color: '#3d1f00' }}>プライベートドライバーを利用したい</p>
                      <p className="text-xs mt-0.5" style={{ color: '#8b6f47' }}>Airport transfer, sightseeing, long-stay support &amp; photo shoot transport</p>
                    </div>
                  </label>
                  {formData.driver_requested && (
                    <div className="mt-4 rounded-lg p-4 space-y-4" style={{ background: '#fff', border: '1.5px dashed #c8a96e' }}>
                      <div>
                        <label className="block text-xs font-bold mb-1.5" style={{ color: '#3d1f00' }}>サービス種別 <span style={{ color: '#c8504a' }}>*</span></label>
                        <select name="driver_service" value={formData.driver_service} onChange={handleChange}
                          className="w-full px-3 py-3 text-sm rounded-lg outline-none transition-all cursor-pointer"
                          style={{ border: '2px solid #c8a96e', background: '#fff', color: '#3d1f00' }}
                          onFocus={(e) => (e.target.style.borderColor = '#8B2500')} onBlur={(e) => (e.target.style.borderColor = '#c8a96e')}>
                          <option value="">サービスを選択</option>
                          <option value="Airport Transfer (Haneda → KOMINKA)">空港送迎（羽田 → KOMINKA）</option>
                          <option value="Airport Transfer (KOMINKA → Haneda)">空港送迎（KOMINKA → 羽田）</option>
                          <option value="Tokyo Sightseeing Tour">東京観光ツアー</option>
                          <option value="Long Stay Support (Shopping / Hospital)">長期滞在サポート（買い物・病院等）</option>
                          <option value="Photo Shoot Location Transport">撮影ロケ送迎</option>
                          <option value="Other">その他</option>
                        </select>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-bold mb-1.5" style={{ color: '#3d1f00' }}>ご希望日 <span style={{ color: '#c8504a' }}>*</span></label>
                          <input type="date" name="driver_date" value={formData.driver_date} onChange={handleChange}
                            className="w-full px-3 py-3 text-sm rounded-lg outline-none transition-all cursor-pointer"
                            style={{ border: '2px solid #c8a96e', background: '#fff', color: '#3d1f00' }}
                            onFocus={(e) => (e.target.style.borderColor = '#8B2500')} onBlur={(e) => (e.target.style.borderColor = '#c8a96e')} />
                        </div>
                        <div>
                          <label className="block text-xs font-bold mb-1.5" style={{ color: '#3d1f00' }}>ご希望時刻 <span style={{ color: '#c8504a' }}>*</span></label>
                          <input type="time" name="driver_time" value={formData.driver_time} onChange={handleChange}
                            className="w-full px-3 py-3 text-sm rounded-lg outline-none transition-all cursor-pointer"
                            style={{ border: '2px solid #c8a96e', background: '#fff', color: '#3d1f00' }}
                            onFocus={(e) => (e.target.style.borderColor = '#8B2500')} onBlur={(e) => (e.target.style.borderColor = '#c8a96e')} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-bold mb-1.5" style={{ color: '#3d1f00' }}>乗車人数 <span style={{ color: '#c8504a' }}>*</span></label>
                        <select name="driver_passengers" value={formData.driver_passengers} onChange={handleChange}
                          className="w-full px-3 py-3 text-sm rounded-lg outline-none transition-all cursor-pointer"
                          style={{ border: '2px solid #c8a96e', background: '#fff', color: '#3d1f00' }}
                          onFocus={(e) => (e.target.style.borderColor = '#8B2500')} onBlur={(e) => (e.target.style.borderColor = '#c8a96e')}>
                          <option value="">人数を選択</option>
                          {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={`${n}名`}>{n}名</option>)}
                        </select>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-bold mb-1.5" style={{ color: '#3d1f00' }}>乗車場所</label>
                          <input type="text" name="driver_pickup" value={formData.driver_pickup} onChange={handleChange} placeholder="例：羽田空港 第3ターミナル"
                            className="w-full px-3 py-3 text-sm rounded-lg outline-none transition-all"
                            style={{ border: '2px solid #c8a96e', background: '#fff', color: '#3d1f00' }}
                            onFocus={(e) => (e.target.style.borderColor = '#8B2500')} onBlur={(e) => (e.target.style.borderColor = '#c8a96e')} />
                        </div>
                        <div>
                          <label className="block text-xs font-bold mb-1.5" style={{ color: '#3d1f00' }}>降車場所</label>
                          <input type="text" name="driver_dropoff" value={formData.driver_dropoff} onChange={handleChange} placeholder="例：KOMINKA（馬込）"
                            className="w-full px-3 py-3 text-sm rounded-lg outline-none transition-all"
                            style={{ border: '2px solid #c8a96e', background: '#fff', color: '#3d1f00' }}
                            onFocus={(e) => (e.target.style.borderColor = '#8B2500')} onBlur={(e) => (e.target.style.borderColor = '#c8a96e')} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold mb-2" style={{ color: '#3d1f00' }}>ご要望・ご質問</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={4} maxLength={500}
                    placeholder="特別なご要望やご質問がございましたらご記入ください（任意・最大500文字）"
                    className="w-full px-4 py-3.5 text-sm rounded-lg outline-none transition-all resize-none" style={inputStyle}
                    onFocus={(e) => (e.target.style.borderColor = '#8B2500')} onBlur={(e) => (e.target.style.borderColor = '#c8a96e')}></textarea>
                  <p className="text-xs mt-1 text-right" style={{ color: '#9a7a50' }}>{formData.message.length} / 500</p>
                </div>

                {status === 'error' && (
                  <div className="flex items-start gap-3 p-4 rounded-lg" style={{ background: '#ffebee', border: '2px solid #f44336' }}>
                    <i className="ri-error-warning-line text-lg flex-shrink-0 mt-0.5" style={{ color: '#f44336' }}></i>
                    <p className="text-sm" style={{ color: '#c62828' }}>送信に失敗しました。もう一度お試しいただくか、直接お問い合わせください。</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full flex items-center justify-center gap-2 px-8 py-4 text-base font-bold rounded-lg transition-all active:scale-95 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg, #8B2500 0%, #c8504a 100%)', color: '#fff', border: '3px solid #6b1c00', minHeight: '56px' }}
                >
                  <i className="ri-send-plane-fill text-lg"></i>
                  {status === 'submitting' ? '送信中...' : '予約リクエストを送信'}
                </button>

                <p className="text-center text-xs mt-1" style={{ color: '#9a7a50' }}>※ 24時間以内にご予約確認のご連絡をいたします</p>
              </form>
            </div>
          </>
        )}
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #c8a96e, #8B2500, #c8a96e)' }} />
    </section>
  );
}
